/**
 * TableSettings – persistence of user-specific table preferences.
 *
 * This utility class centralises reading and writing all table-related user settings
 * (page size, column widths, sort order, frozen columns). Settings are kept as a
 * JSON string in the Vuex store under `userSettings.tableSettings` and persisted
 * server-side via the UserApi so they survive page reloads and browser restarts.
 *
 * All entries are indexed by route name so that different table views maintain
 * independent settings.
 */

import {ToastManager} from "@/util/ToastManager";
import {UserApi} from "@dex/squeeze-client-ts";
import {Store} from "vuex";

export interface TableListSettings {
	pageSize?: number;
	page?: number;
	pageCount?: number;
}

export interface TableColumnWidthV2 {
	tableFieldId: number;
	width: string;
}

export class TableSettings {

	/**
	 * Parse tableSettings from store into a deep-cloned object.
	 */
	private static parseSettings(store: any): Record<string, any> {
		try {
			if (!store.state.userSettings.tableSettings) {
				return {};
			}
			return JSON.parse(store.state.userSettings.tableSettings);
		} catch {
			return {};
		}
	}

	/**
	 * Get current rows per page for a route.
	 * @param store
	 * @param routeName
	 */
	public static getTableListPagination(store: any, routeName: any): number {
		try {
			const settings = this.parseSettings(store);
			return settings[routeName]?.pageSize ?? 25;
		} catch {
			return 25;
		}
	}

	/**
	 * Save rows per page for a route. Merges into the existing route entry instead of replacing it.
	 * @param t
	 * @param toast
	 * @param store
	 * @param userApi
	 * @param routeName
	 * @param rows
	 */
	public static saveTableListPagination(t: any, toast: any, store: Store<any>, userApi: UserApi, routeName: any, rows: number) {
		try {
			const settings = this.parseSettings(store);
			const currentRows: number = settings[routeName]?.pageSize ?? 25;

			if (currentRows === rows) return;

			if (rows === 25) {
				if (settings[routeName]) {
					delete settings[routeName].pageSize;
					if (Object.keys(settings[routeName]).length === 0) {
						delete settings[routeName];
					}
				}
			} else {
				if (!settings[routeName]) {
					settings[routeName] = {};
				}
				settings[routeName].pageSize = rows;
			}

			store.state.userSettings.tableSettings = JSON.stringify(settings);
			this.saveUserSettings(t, toast, store, userApi, settings);
		} catch(error: unknown) {
			store.dispatch("resetStore");
		}
	}

	/**
	 * Save document list table column widths.
	 * Column widths are kept in their own namespace as they carry large payloads.
	 * @param t
	 * @param toast
	 * @param store
	 * @param userApi
	 * @param documentClassId
	 * @param columnWidths
	 */
	public static saveDocumentListTableColumnWidths(t: any, toast: any, store: Store<any>, userApi: UserApi, documentClassId: number, columnWidths: any[]) {
		const currentSettings = this.parseSettings(store);

		if (!currentSettings.documentList) {
			currentSettings.documentList = {};
		}

		if (columnWidths.length === 0) {
			if (currentSettings.documentList[documentClassId]) {
				delete currentSettings.documentList[documentClassId];
				if (Object.keys(currentSettings.documentList).length === 0) {
					delete currentSettings.documentList;
				}
			}
		} else {
			currentSettings.documentList[documentClassId] = columnWidths;
		}

		store.state.userSettings.tableSettings = JSON.stringify(currentSettings);
		this.saveUserSettings(t, toast, store, userApi, currentSettings);
	}

	// ─── Generic sub-setting helpers ─────────────────────────────────────────────
	//
	// All sort and frozenColumns data is stored under a unified route-name key:
	//
	//   {
	//     "DocumentListView": {
	//       "sort":          { "<documentClassId>": [ { field, order }, ... ] },
	//       "frozenColumns": { "<documentClassId>": { columnName: true, ... } },
	//       "pageSize": 100
	//     },
	//     "AdminQueueList": {
	//       "sort": { "<stepName>": { field, order } },
	//       "pageSize": 25
	//     }
	//   }
	//
	// Usage:
	//   get:  TableSettings.getTableSubSetting<{ field: string; order: number }[]>(store, route.name, 'sort', documentClassId)
	//   save: TableSettings.saveTableSubSetting(t, toast, store, userApi, route.name, 'sort', documentClassId, sort, v => v.length === 0)

	/**
	 * Get a sub-setting: settings[routeName][property][subKey]
	 * @param store
	 * @param routeName - route-level key (e.g. "DocumentListView")
	 * @param property  - setting category (e.g. "sort", "frozenColumns")
	 * @param subKey    - instance key (e.g. documentClassId, stepName)
	 */
	public static getTableSubSetting<T>(store: any, routeName: string, property: string, subKey: string | number): T | null {
		try {
			const settings = this.parseSettings(store) ?? null;
			return (settings[routeName]?.[property]?.[subKey] ?? null) as T | null;
		} catch {
			return null;
		}
	}

	/**
	 * Save a sub-setting: settings[routeName][property][subKey] = value
	 * Cleans up empty parent objects after deletion.
	 * @param t
	 * @param toast
	 * @param store
	 * @param userApi
	 * @param routeName - route-level key (e.g. "DocumentListView")
	 * @param property  - setting category (e.g. "sort", "frozenColumns")
	 * @param subKey    - instance key (e.g. documentClassId, stepName)
	 * @param value
	 * @param isEmpty   - when true the entry is removed; defaults to value == null
	 */
	public static saveTableSubSetting<T>(t: any, toast: any, store: Store<any>, userApi: UserApi, routeName: string, property: string, subKey: string | number, value: T, isEmpty?: (v: T) => boolean): void {
		const settings = this.parseSettings(store);
		const shouldDelete = isEmpty ? isEmpty(value) : value == null;

		if (shouldDelete) {
			if (settings[routeName]?.[property]?.[subKey] !== undefined) {
				delete settings[routeName][property][subKey];
				if (Object.keys(settings[routeName][property]).length === 0) {
					delete settings[routeName][property];
					if (Object.keys(settings[routeName]).length === 0) {
						delete settings[routeName];
					}
				}
			}
		} else {
			if (!settings[routeName]) {
				settings[routeName] = {};
			}
			if (!settings[routeName][property]) {
				settings[routeName][property] = {};
			}
			settings[routeName][property][subKey] = value;
		}

		store.state.userSettings.tableSettings = JSON.stringify(settings);
		this.saveUserSettings(t, toast, store, userApi, settings);
	}

	/**
	 * Save the current view settings together with existing table settings.
	 * @param t
	 * @param toast
	 * @param store
	 * @param userApi
	 */
	public static saveViewSettings(t: any, toast: any, store: Store<any>, userApi: UserApi) {
		const currentTableSettings = store.state.userSettings.tableSettings.length
			? JSON.parse(JSON.stringify(JSON.parse(store.state.userSettings.tableSettings)))
			: {};

		this.saveUserSettings(t, toast, store, userApi, currentTableSettings);
	}

	/**
	 * Save user settings
	 * @param t
	 * @param toast
	 * @param store
	 * @param userApi
	 * @param settings
	 */
	private static saveUserSettings = (t: any, toast: any, store: Store<any>, userApi: UserApi, settings: any) => {
		const userSettingsSave = JSON.parse(JSON.stringify(store.state.userSettings));
		userSettingsSave.tableSettings = JSON.stringify(settings);
		userSettingsSave.viewSettings = JSON.stringify(store.state.userSettings.viewSettings);
		userApi.putAllUserProperties(userSettingsSave)
			.catch((response: Response) => response.json().then ((err: { message: string }) => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
			}));
	}

}
